import { useMemo } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./styles.module.scss";

export interface CountPickerComponentProps extends PlatformComponentProps {
  title: string;
  subtitle?: string;
  minimumCount?: number;
  maximumReached?: boolean;
  count: number;
  setCount: (count: number) => void;
}

export const CountPicker = ({
  title,
  subtitle,
  className,
  minimumCount,
  count,
  setCount,
  maximumReached = false,
}: CountPickerComponentProps) => {
  const COMPONENT_KEY = CoreUiComponents.CountPicker;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const minusDisabled = useMemo(() => {
    return count <= (minimumCount ?? 0);
  }, [count, minimumCount]);

  return (
    <div className={clsx(block, className)}>
      <div className={cn("label")}>
        <div className={cn("title")}>{title}</div>
        {subtitle != null && subtitle !== "" ? (
          <div className={cn("subtitle")}>{subtitle}</div>
        ) : null}
      </div>
      <div className={cn("picker-container")}>
        <button
          aria-label={`${t(
            "core-flights.search.decreaseAmountOfTravelersByCategory",
            {
              guestType: title,
            }
          )}`}
          className={cn("counter-button")}
          disabled={minusDisabled}
          onClick={() => {
            setCount(count - 1);
          }}
        >
          <Icon iconName={IconNameEnum.minus} />
        </button>

        <div className={cn("guest-count")}>
          {count}
          <span className={"sr-only"} aria-live="polite" aria-atomic={true}>
            `{count} {title} ${subtitle != null ? subtitle : ""}`
          </span>
        </div>

        <button
          aria-label={t("core-hotels.search.guestPicker.increaseGuestAmount", {
            guestType: title,
          })}
          className={clsx(cn("counter-button"))}
          onClick={() => setCount(count + 1)}
          disabled={maximumReached}
        >
          <Icon iconName={IconNameEnum.plus} />
        </button>
      </div>
    </div>
  );
};
